import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import redditApi from '../../../services/redditApi';
import { clearbitLogoFromWebsite } from '../../../utils/clearbitLogo';
import RhMonthPicker from './RhMonthPicker';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return `${MONTHS_SHORT[m - 1]} ${y}`;
}

function currentYearMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Export a real .xlsx workbook (Office Open XML).
 * Works on Windows (Excel), macOS (Excel / Numbers), and Linux (LibreOffice),
 * plus Google Sheets. Month labels merge across Live + Remaining.
 */
function exportReportExcel(report) {
  const months = report.months || [];
  const aoa = [];

  // Row 1: Client Name | Quota | Shortfall | May 2026 | (merge) | Jun 2026 | (merge) | ...
  const headerRow1 = ['Client Name', 'Quota Per Month', 'Shortfall'];
  for (const m of months) {
    headerRow1.push(formatMonthLabel(m), '');
  }
  aoa.push(headerRow1);

  // Row 2: blank | blank | blank | Live | Remaining | Live | Remaining | ...
  const headerRow2 = ['', '', ''];
  for (let i = 0; i < months.length; i += 1) {
    headerRow2.push('Live', 'Remaining');
  }
  aoa.push(headerRow2);

  for (const row of report.clients || []) {
    const cells = [
      row.client_name,
      row.quota_per_month ?? '',
      row.shortfall ?? '',
    ];
    for (const m of months) {
      const cell = row.months?.[m];
      if (!cell) {
        cells.push('', '');
      } else {
        cells.push(cell.live ?? 0, cell.remaining ?? '');
      }
    }
    aoa.push(cells);
  }

  const totalCells = ['TOTAL', '', report.total_shortfall ?? ''];
  for (const m of months) {
    const t = report.totals_by_month?.[m];
    totalCells.push(t?.live ?? 0, t?.remaining ?? '');
  }
  aoa.push(totalCells);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Merge each month header across its Live + Remaining columns.
  // Cols: 0=Client, 1=Quota, 2=Shortfall, then pairs starting at col 3.
  ws['!merges'] = months.map((_, i) => {
    const startCol = 3 + i * 2;
    return { s: { r: 0, c: startCol }, e: { r: 0, c: startCol + 1 } };
  });

  ws['!cols'] = [
    { wch: 28 },
    { wch: 16 },
    { wch: 12 },
    ...months.flatMap(() => [{ wch: 10 }, { wch: 12 }]),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Monthly Client Report');
  XLSX.writeFile(
    wb,
    `monthly-client-report-${report.from}-to-${report.to}.xlsx`,
    { bookType: 'xlsx' },
  );
}

/* ─── Multi-select client picker ─────────────────────────────────────────── */
/*
 * Selection model:
 *   null      → all clients (default)
 *   []        → none selected
 *   string[]  → explicit subset
 *
 * (Previously [] meant "all" and null meant "none", which crashed on
 *  selected.length whenever Clear set null.)
 */

function ClientMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const safeOptions = Array.isArray(options) ? options : [];
  const isAll = selected === null;
  const selectedList = Array.isArray(selected) ? selected : [];
  const isNone = !isAll && selectedList.length === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return safeOptions;
    return safeOptions.filter((n) => String(n).toLowerCase().includes(q));
  }, [safeOptions, search]);

  function openMenu() {
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 250;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 280 && rect.top > 280;
    setMenuStyle({
      position: 'fixed',
      width: menuWidth,
      zIndex: 9999,
      left: Math.min(rect.left, window.innerWidth - menuWidth - 12),
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    }
    // Close on page/container scroll, but not when scrolling inside the menu itself.
    function handleScroll(e) {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  function toggle(name) {
    // All selected → first uncheck becomes "all except this one"
    if (isAll) {
      onChange(safeOptions.filter((n) => n !== name));
      return;
    }
    if (selectedList.includes(name)) {
      onChange(selectedList.filter((n) => n !== name));
      return;
    }
    const next = [...selectedList, name].sort((a, b) => a.localeCompare(b));
    // Full set again → collapse back to "all"
    if (safeOptions.length > 0 && next.length === safeOptions.length) onChange(null);
    else onChange(next);
  }

  function selectAll() {
    onChange(null);
  }

  function clearAll() {
    onChange([]);
  }

  const label = (() => {
    if (isAll) return `All clients (${safeOptions.length})`;
    if (isNone) return 'No clients';
    if (selectedList.length === 1) return selectedList[0];
    return `${selectedList.length} clients`;
  })();

  const isChecked = (name) => {
    if (isAll) return true;
    return selectedList.includes(name);
  };

  return (
    <div style={{ position: 'relative', width: 250 }}>
      <button
        type="button"
        ref={triggerRef}
        className="rh-custom-select-trigger"
        style={{ width: '100%' }}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg className="rh-custom-select-chevron" width="12" height="12" fill="none"
          stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="rh-custom-select-menu"
          style={{ ...menuStyle, maxHeight: 320, display: 'flex', flexDirection: 'column', padding: 0 }}
        >
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--rh-border)' }}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients…"
              autoFocus
              style={{
                width: '100%', border: '1px solid var(--rh-border)', borderRadius: 6,
                padding: '6px 10px', fontSize: 13, outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={selectAll}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--rh-orange)', padding: 0,
                }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)', padding: 0,
                }}
              >
                Clear
              </button>
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--rh-text-faint)' }}>
                No clients match
              </div>
            )}
            {filtered.map((name) => {
              const checked = isChecked(name);
              return (
                <button
                  key={name}
                  type="button"
                  className={`rh-custom-select-option${checked ? ' selected' : ''}`}
                  onClick={() => toggle(name)}
                  style={{ width: '100%', justifyContent: 'flex-start', gap: 8 }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${checked ? 'var(--rh-orange)' : 'var(--rh-border)'}`,
                    background: checked ? 'var(--rh-orange)' : '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {checked && (
                      <svg width="10" height="10" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  {name}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function cellNum(v) {
  if (v === null || v === undefined) return '—';
  return v;
}

function ClientLogo({ client, size = 20 }) {
  const [err, setErr] = useState(false);
  const src = client?.logo_url || clearbitLogoFromWebsite(client?.website);
  useEffect(() => { setErr(false); }, [src]);
  const initials = (client?.client_name || '?').trim().charAt(0).toUpperCase();
  if (!src || err) {
    return (
      <span style={{
        width: size, height: size, borderRadius: 6, flexShrink: 0,
        background: '#f1f5f9', color: '#475569', fontSize: size * 0.5, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{initials}</span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: 6, flexShrink: 0, objectFit: 'contain', background: '#fff' }}
    />
  );
}

export default function MonthlyClientReportTab() {
  const nowYm = currentYearMonth();
  const [fromMonth, setFromMonth] = useState(nowYm);
  const [toMonth, setToMonth] = useState(nowYm);
  // null = all clients; [] = none; string[] = subset
  const [selectedClients, setSelectedClients] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientMeta, setClientMeta] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState('');

  useEffect(() => {
    setLoadingClients(true);
    redditApi.get('/admin/clients/')
      .then(({ data }) => {
        const clients = data?.clients || [];
        const names = clients
          .map((c) => c.client_name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        const meta = {};
        for (const c of clients) {
          if (!c?.client_name) continue;
          meta[c.client_name] = {
            client_name: c.client_name,
            logo_url: c.logo_url || '',
            website: c.website || '',
          };
        }
        setClientOptions(names);
        setClientMeta(meta);
      })
      .catch(() => {
        setClientOptions([]);
        setClientMeta({});
      })
      .finally(() => setLoadingClients(false));
  }, []);

  function validate() {
    if (!fromMonth || !toMonth) return 'Select both From and To months.';
    if (toMonth < fromMonth) return 'To month must be on or after From month.';
    if (Array.isArray(selectedClients) && selectedClients.length === 0) {
      return 'Select at least one client.';
    }
    return '';
  }

  async function generate() {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError('');
    setOverview('');
    setReport(null);
    try {
      const params = { from: fromMonth, to: toMonth };
      if (Array.isArray(selectedClients) && selectedClients.length > 0) {
        params.clients = selectedClients.join(',');
      }
      const { data } = await redditApi.get('/admin/monthly-client-report/', { params });
      if (!data || typeof data !== 'object') {
        setError('Failed to generate report.');
        setReport(null);
        setOverview('');
        return;
      }

      const clients = Array.isArray(data.clients) ? data.clients : [];
      const months = Array.isArray(data.months) ? data.months : [];
      const normalized = {
        ...data,
        clients,
        months,
        totals_by_month: data.totals_by_month || {},
      };

      let overviewText = '';
      if (clients.length > 0) {
        try {
          const { data: ov } = await redditApi.post(
            '/admin/monthly-client-report/overview/',
            normalized,
          );
          overviewText = typeof ov?.overview === 'string' ? ov.overview : '';
        } catch {
          // Overview is optional — still show the numbers if it fails.
          overviewText = '';
        }
      }

      setOverview(overviewText);
      setReport(normalized);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate report.');
      setReport(null);
      setOverview('');
    } finally {
      setLoading(false);
    }
  }

  const months = report?.months || [];
  const reportClients = report?.clients || [];

  return (
    <div className="rh-tab-scroll">
      <div className="rh-page-header">
        <h2 className="rh-page-title">Monthly Client Report</h2>
        <p className="rh-page-subtitle">
          Quota shortfall by month — only LIVE comments count toward delivery
        </p>
      </div>

      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 20,
        flexWrap: 'wrap', padding: '14px 16px',
        background: '#fff', border: '1px solid var(--rh-border)', borderRadius: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 250 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>Clients</label>
          <ClientMultiSelect
            options={clientOptions}
            selected={selectedClients}
            onChange={setSelectedClients}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>From</label>
          <RhMonthPicker value={fromMonth} onChange={setFromMonth} placeholder="From month" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--rh-text-muted)' }}>To</label>
          <RhMonthPicker value={toMonth} onChange={setToMonth} placeholder="To month" />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <button
            type="button"
            className="rh-btn-primary"
            onClick={generate}
            disabled={loading || loadingClients}
            style={{ height: 34, padding: '0 18px', whiteSpace: 'nowrap' }}
          >
            {loading ? 'Generating…' : 'Generate'}
          </button>
          {report && (reportClients.length > 0) && (
            <button
              type="button"
              className="rh-btn-secondary"
              onClick={() => exportReportExcel(report)}
              style={{
                height: 34, padding: '0 14px', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export Excel
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 8,
          background: '#fef2f2', color: '#b91c1c', fontSize: 13, fontWeight: 500,
          border: '1px solid #fecaca',
        }}>
          {error}
        </div>
      )}

      {!report && !loading && !error && (
        <div style={{
          padding: '48px 24px', textAlign: 'center',
          color: 'var(--rh-text-faint)', fontSize: 14,
          border: '1px dashed var(--rh-border)', borderRadius: 12, background: '#fafafa',
        }}>
          Choose a month range and click Generate to build the quota shortfall report.
        </div>
      )}

      {loading && (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: 14 }}>
          Generating report…
        </div>
      )}

      {report && !loading && (
        <>
          {reportClients.length === 0 ? (
            <div style={{
              padding: '32px 24px', textAlign: 'center',
              color: 'var(--rh-text-faint)', fontSize: 14,
              border: '1px dashed var(--rh-border)', borderRadius: 12,
            }}>
              No clients with comments in this range.
            </div>
          ) : (
            <>
              {overview && (
                <div style={{
                  marginBottom: 20,
                  background: '#fff',
                  border: '1px solid var(--rh-border)',
                  borderRadius: 12,
                  padding: '18px 20px',
                }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: 'var(--rh-text)', marginBottom: 12,
                  }}>
                    Report Overview
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--rh-text)' }}>
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p style={{ margin: '0 0 10px' }}>{children}</p>,
                        ul: ({ children }) => (
                          <ul style={{ margin: '0 0 10px', paddingLeft: 20 }}>{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol style={{ margin: '0 0 10px', paddingLeft: 20 }}>{children}</ol>
                        ),
                        li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
                        strong: ({ children }) => (
                          <strong style={{ fontWeight: 700 }}>{children}</strong>
                        ),
                      }}
                    >
                      {overview}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              <div style={{
                overflowX: 'auto', background: '#fff',
                border: '1px solid var(--rh-border)', borderRadius: 12,
              }}>
                <table
                  className="rh-table"
                  style={{
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    width: '100%',
                    minWidth: 300 + 105 * (2 + months.length * 2),
                  }}
                >
                <colgroup>
                  <col style={{ width: 300 }} />
                  <col />
                  <col />
                  {months.flatMap((m) => [
                    <col key={`${m}-live`} />,
                    <col key={`${m}-rem`} />,
                  ])}
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{
                      position: 'sticky', left: 0, zIndex: 2, background: '#f8fafc',
                      width: 300, maxWidth: 300, minWidth: 300,
                      fontWeight: 700,
                    }}>
                      Client Name
                    </th>
                    <th rowSpan={2} style={{ minWidth: 105, textAlign: 'center', fontWeight: 700 }}>Quota</th>
                    <th rowSpan={2} style={{
                      minWidth: 105, textAlign: 'center', fontWeight: 700,
                      borderRight: '2px solid #94a3b8',
                    }}>
                      Shortfall
                    </th>
                    {months.map((m) => (
                      <th
                        key={m}
                        colSpan={2}
                        style={{
                          textAlign: 'center',
                          borderBottom: '1px solid var(--rh-border)',
                          borderLeft: '2px solid #94a3b8',
                          borderRight: '2px solid #94a3b8',
                          minWidth: 210,
                          fontWeight: 700,
                          background: '#f1f5f9',
                        }}
                      >
                        {formatMonthLabel(m)}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {months.map((m) => (
                      <React.Fragment key={`${m}-sub`}>
                        <th style={{
                          fontWeight: 700, fontSize: 11, color: 'var(--rh-text-muted)',
                          minWidth: 105, textAlign: 'center',
                          borderLeft: '2px solid #94a3b8',
                          background: '#f8fafc',
                        }}>Live</th>
                        <th style={{
                          fontWeight: 700, fontSize: 11, color: 'var(--rh-text-muted)',
                          minWidth: 105, textAlign: 'center',
                          borderRight: '2px solid #94a3b8',
                          background: '#f8fafc',
                        }}>Remaining</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportClients.map((row) => (
                    <tr key={row.client_name}>
                      <td style={{
                        position: 'sticky', left: 0, background: '#fff',
                        fontWeight: 600, whiteSpace: 'nowrap',
                        width: 300, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          maxWidth: '100%', overflow: 'hidden',
                        }}>
                          <ClientLogo
                            client={clientMeta[row.client_name] || { client_name: row.client_name }}
                            size={20}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.client_name}</span>
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{cellNum(row.quota_per_month)}</td>
                      <td style={{
                        textAlign: 'center', fontWeight: 700,
                        borderRight: '2px solid #94a3b8',
                        color: (row.shortfall || 0) > 0 ? '#b91c1c' : (row.shortfall || 0) < 0 ? '#15803d' : undefined,
                      }}>
                        {cellNum(row.shortfall)}
                      </td>
                      {months.map((m) => {
                        const cell = row.months?.[m];
                        if (!cell) {
                          return (
                            <React.Fragment key={`${row.client_name}-${m}`}>
                              <td style={{
                                textAlign: 'center', color: 'var(--rh-text-faint)',
                                borderLeft: '2px solid #94a3b8', fontWeight: 700,
                              }} />
                              <td style={{
                                textAlign: 'center', color: 'var(--rh-text-faint)',
                                borderRight: '2px solid #94a3b8', fontWeight: 700,
                              }} />
                            </React.Fragment>
                          );
                        }
                        const rem = cell.remaining;
                        return (
                          <React.Fragment key={`${row.client_name}-${m}`}>
                            <td style={{
                              textAlign: 'center', fontWeight: 700,
                              borderLeft: '2px solid #94a3b8',
                            }}>
                              {cell.live}
                            </td>
                            <td style={{
                              textAlign: 'center',
                              fontWeight: 700,
                              borderRight: '2px solid #94a3b8',
                              color: rem > 0 ? '#b91c1c' : rem < 0 ? '#15803d' : undefined,
                            }}>
                              {cellNum(rem)}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                    <td style={{
                      position: 'sticky', left: 0, background: '#f8fafc',
                      width: 300, maxWidth: 300,
                    }}>Total</td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }} />
                    <td style={{
                      textAlign: 'center', fontWeight: 700,
                      borderRight: '2px solid #94a3b8',
                      color: (report.total_shortfall || 0) > 0 ? '#b91c1c' : undefined,
                    }}>
                      {report.total_shortfall ?? 0}
                    </td>
                    {months.map((m) => {
                      const t = report.totals_by_month?.[m];
                      return (
                        <React.Fragment key={`total-${m}`}>
                          <td style={{
                            textAlign: 'center', fontWeight: 700,
                            borderLeft: '2px solid #94a3b8',
                          }}>
                            {t?.live ?? 0}
                          </td>
                          <td style={{
                            textAlign: 'center', fontWeight: 700,
                            borderRight: '2px solid #94a3b8',
                          }}>
                            {cellNum(t?.remaining)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
